import React from 'react';
import {ViewMode} from '@taopix/taopix-core';
import {Button, Theme, ThemeName, ZoomInIcon, ZoomOutIcon} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import useViewMode from '../../Hooks/useViewMode';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';

const GridSizeZoomButtons = () => {
  const ui = useUiState();
  const {t} = useTranslation();
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const minGridSize = 120;
  const maxGridSize = 600;
  const zoomPercent = 10;

  const changeGridSize = (value: number) => {
    const newGridSize = Math.min(maxGridSize, Math.max(minGridSize, value)); // Keep the grid size between the min and max
    ui.dispatch(UserInterfaceActions.setGridSize(newGridSize));
    ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(false));
  };

  const gridSizeToolbarClasses = classNames(
    'flex',
    'shadow-md',
    'rounded-themeCornerSize',
    'transition-[transform]',
    'ease-[cubic-bezier(0.42,0,0,2.27)]'
  );

  // Don't show the grid size selector if not in grid mode or it is not configured in the experience
  if (
    !experience.EDITOR.workArea.gridView.gridSizeControl ||
    (viewMode !== ViewMode.Grid && viewMode !== ViewMode.Preview) ||
    (viewMode === ViewMode.Preview && ui.state.previewViewMode !== ViewMode.Grid)
  ) {
    return <></>;
  }

  return (
    <>
      {!ui.state.isSmallScreen && (
        <>
          <Theme name={ThemeName.Popouts} className={gridSizeToolbarClasses}>
            <Button
              label={t('str_LabelSmallGridTiles')}
              startIcon={<ZoomOutIcon />}
              buttonStyle={'standard'}
              tabPosition={'left'}
              size={'small'}
              hideLabel
              onClick={() => {
                const newGridSize = ui.state.gridSize
                  ? ui.state.gridSize * (1 - zoomPercent / 100)
                  : 200;
                changeGridSize(newGridSize);
                ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(false));
              }}
              className={'rounded-l-themeCornerSize'}
              corners={'square'}
              disabled={ui.state.gridSize !== null && ui.state.gridSize <= minGridSize}
            />
            <Button
              label={t('str_LabelLargeGridTiles')}
              startIcon={<ZoomInIcon />}
              buttonStyle={'standard'}
              tabPosition={'right'}
              size={'small'}
              hideLabel
              onClick={() => {
                const newGridSize = ui.state.gridSize
                  ? ui.state.gridSize * (1 + zoomPercent / 100)
                  : 200;
                changeGridSize(newGridSize);
                ui.dispatch(UserInterfaceActions.setAutoGridSizeSwitching(false));
              }}
              className={'rounded-r-themeCornerSize'}
              corners={'square'}
              disabled={ui.state.gridSize !== null && ui.state.gridSize >= maxGridSize}
            />
          </Theme>
        </>
      )}
    </>
  );
};

export default GridSizeZoomButtons;
